import { forwardRef, type ReactNode, type Ref, type RefObject } from 'react';
import clsx from 'clsx';
import { useModalSheetInputIosKeyboard } from '../useModalSheetInputIosKeyboard';
import { ModalContent } from './ModalContent/ModalContent';
import { ModalOverlay } from './ModalOverlay/ModalOverlay';
import {
  useModalTransition,
  type ModalTransitionResult,
} from './useModalTransition';
import styles from './Modal.module.css';

export interface ModalProps {
  children: ReactNode;
  isOpen: boolean;
  ariaLabel: string;
  testid?: string;
  onDismiss?: () => void;
  contentClassName?: string;
  overlayClassName?: string;
  innerContentClassName?: string;
  onAnimationEnd?: () => void;
  initialFocusRef?: RefObject<HTMLElement>;
  onRest?: (result: ModalTransitionResult) => void;
  /**
   * When true, disables pointer events during the leave animation to prevent
   * clicks on closing modal content. Useful for nested modals on mobile.
   */
  disablePointerEventsOnClose?: boolean;
}

export const Modal = forwardRef(
  (props: ModalProps, ref: Ref<HTMLDivElement>) => {
    const {
      contentClassName,
      overlayClassName,
      innerContentClassName,
      isOpen,
      onDismiss,
      testid,
      ariaLabel,
      children,
      onAnimationEnd,
      initialFocusRef,
      onRest,
      disablePointerEventsOnClose,
    } = props;

    const { shouldRender, closing, handleTransitionEnd } = useModalTransition({
      isOpen,
      onRest,
    });

    const { overlayRef } = useModalSheetInputIosKeyboard();

    if (!shouldRender) {
      return null;
    }

    return (
      <ModalOverlay
        ref={(element) => {
          overlayRef.current = element;
        }}
        onDismiss={onDismiss}
        initialFocusRef={initialFocusRef}
        className={clsx(
          styles.overlay,
          overlayClassName,
          closing && styles.closing,
          closing && disablePointerEventsOnClose && styles['no-pointer-events']
        )}
        data-testid={testid}
        isOpen={shouldRender}
        onAnimationEnd={onAnimationEnd}
        onTransitionEnd={handleTransitionEnd}
      >
        <ModalContent
          ref={ref}
          className={clsx(styles.content, contentClassName)}
          ariaLabel={ariaLabel}
        >
          <div className={innerContentClassName}>{children}</div>
        </ModalContent>
      </ModalOverlay>
    );
  }
);
